// 商品情報の利用設定 モデル（openspec: product-info-usage-settings）

/** 商品情報の項目（マトリクスの行） */
export interface PiuField {
  key: string
  label: string
  /** 表示用。どのマスタ項目から来ているか */
  source: string
  /** 利用対象から外せない項目（商品名）。外すと索引が空になる */
  required: boolean
}

/** 用途（マトリクスの列） */
export interface PiuUsage {
  key: string
  label: string
  description: string
  /** 反映先の索引。dictionary / vector / recommend */
  target: string
  /** この用途で設定できる項目キー。ここに無い組み合わせは使わない */
  applicable_fields: string[]
  /** 設定できない組み合わせの理由 {field_key: 理由}。理由なしで使えないと見せない */
  not_applicable_reasons: Record<string, string>
  /** 変更しても商品意味理解を実行し直すまで完全には効かない項目 */
  re_extraction_fields: string[]
}

/** 再抽出しないと効かない設定変更の1件 */
export interface PiuReExtractionChange {
  usage_key: string
  field_key: string
  enabled: boolean
}

/** 商品意味理解の状態。注意書きを出すかの判断に使う */
export interface PiuProductUnderstandingState {
  active: boolean
  last_extracted_at: string | null
  /** 抽出より後に変更された「再抽出が要る項目」 */
  changed_since_extract: PiuReExtractionChange[]
}

/** {usage_key: {field_key: 適用するか}} */
export type PiuMatrix = Record<string, Record<string, boolean>>

/** 反映履歴の1件 */
export interface PiuApplication {
  id: number
  targets: string[]
  status: 'running' | 'complete' | 'error'
  /** 反映した時点の設定。現在の索引がどの設定で作られたかを示す */
  settings_snapshot: PiuMatrix
  result: Record<string, PiuApplicationResult> | null
  error: string | null
  triggered_by_name: string | null
  started_at: string | null
  finished_at: string | null
  elapsed_sec: number | null
}

export interface PiuApplicationResult {
  ok: boolean
  elapsed_sec: number
  error?: string
  [key: string]: unknown
}

export interface PiuOverview {
  fields: PiuField[]
  usages: PiuUsage[]
  matrix: PiuMatrix
  /** 未反映の反映先。保存したが索引を作り直していないもの */
  pending_targets: string[]
  last_saved_at: string | null
  running_application: PiuApplication | null
  applications: PiuApplication[]
  product_understanding: PiuProductUnderstandingState
}

/** 用途の表示名。反映履歴のスナップショットのように、
 *  用途の定義（API）を持っていない場所で使う */
export const PIU_USAGE_LABEL: Record<string, string> = {
  keyword_search: '語句検索',
  semantic: '意味検索・会話',
  recommend: 'レコメンド'
}

/** 項目の表示名。用途と同じく、定義を持たない場所での表示に使う */
export const PIU_FIELD_LABEL: Record<string, string> = {
  name: '商品名',
  name_kana: '商品名カナ（読み）',
  search_keyword: '検索キーワード',
  catchcopy: 'キャッチコピー',
  outline: '商品概要',
  desc_detail: '詳細説明',
  maker: 'メーカー名',
  tag: '商品タグ',
  category: 'カテゴリ',
  brand: 'ブランド',
  variation: 'バリエーション',
  price: '価格'
}

/** 反映先の表示名 */
export const PIU_TARGET_LABEL: Record<string, string> = {
  dictionary: '検索サジェスト辞書',
  vector: 'ベクトル（意味検索・会話）',
  recommend: 'レコメンド学習'
}
